/**
 * The one way a sheet comes up from the bottom.
 *
 * Every bottom sheet in the app did this its own way: some appeared with no
 * motion at all, most sat underneath the bottom nav because the nav is painted
 * by the shell and the sheet by the page. So the report sheet's Send button
 * was under the Cards tab.
 *
 * This wrapper owns three things so no sheet has to think about them:
 *   - the nav steps out of the way while the sheet is up, and comes back after,
 *   - the scrim fades and the sheet slides up, and slides back down on close,
 *   - a tap on the scrim closes, a tap on the sheet does not.
 *
 * Callers keep their own styles for the scrim and the sheet; only the motion
 * and the nav are shared.
 */

import { useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UIContext } from '../context/UIContext';

const SHEET_SPRING = { type: 'spring', stiffness: 380, damping: 34, mass: 0.9 };

/**
 * For sheets that already animate themselves: the nav steps aside while they
 * are up. The nav is painted by the shell and a sheet by the page, and on the
 * phone the shell won that fight whatever the sheet's z-index said.
 */
export function useHideNavWhile(open) {
  // Read directly rather than through useUI, which throws without a provider.
  // A sheet rendered on its own, in a test or a harness, has no nav to hide.
  const ui = useContext(UIContext);
  const push = ui?.pushHideNav;
  const pop = ui?.popHideNav;
  useEffect(() => {
    if (!open || !push || !pop) return undefined;
    push();
    return () => pop();
  }, [open, push, pop]);
}

export default function BottomSheet({
  open,
  onClose,
  overlayStyle,
  sheetStyle,
  label = 'Sheet',
  children,
}) {
  useHideNavWhile(open);

  // Rendered on document.body.
  //
  // A z-index only outranks siblings inside its own stacking context, and a
  // sheet opened from inside the page scroller is not a sibling of the things
  // that cover it - the header's tab pill, the bottom nav, a docked action bar
  // portalled to the body by somebody else. Its z-index can be larger and still
  // lose. That bug was found three separate times in one day: the sort sheet
  // under the tab pill, the master set sheet under its own "chase this set"
  // bar, and the binder chooser under the selection bar. Hiding the nav, which
  // this file already did, only ever solved one of the three.
  //
  // Portalling lifts every sheet into the document's own stacking context,
  // where the numbers mean what they look like they mean. Callers keep their
  // own scrim and sheet styles; nothing about their appearance changes.
  const tree = (
    <AnimatePresence>
      {open && (
        <motion.div
          style={overlayStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            style={sheetStyle}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%', transition: { duration: 0.18, ease: 'easeIn' } }}
            transition={SHEET_SPRING}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={label}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return tree;
  return createPortal(tree, document.body);
}
